import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, DatePicker, Textarea } from '~/components/primitives';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import {
  type StockEntryUpdateBody,
  fetchStockEntry,
  updateStockEntry,
} from '~/lib/api/stock-entries';
import shared from './_auth._shared.module.css';

/**
 * EFE-8 — Stock Entry edit (header-only: posting_date + remarks). Lines edit
 * via separate wizard refactor (TBD). Server enforces state guard.
 */
export const Route = createFileRoute('/_auth/stock-entries/$id/edit')({
  component: StockEntryEdit,
});

function StockEntryEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['stock-entries', id],
    queryFn: () => fetchStockEntry(id),
  });

  const [form, setForm] = useState({
    posting_date: '',
    notes_ru: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const se = query.data;
      setForm({
        posting_date: se.posting_at?.slice(0, 10) ?? '',
        notes_ru: se.notes ?? '',
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    mutationFn: () => {
      const se = query.data;
      const body: StockEntryUpdateBody = {
        posting_date: form.posting_date,
        notes_ru: form.notes_ru || null,
        // EFE-14: optimistic concurrency.
        expected_updated_at: (se as { updated_at?: string })?.updated_at,
      };
      return updateStockEntry(id, body);
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: 'Документ обновлён' });
      void qc.invalidateQueries({ queryKey: ['stock-entries', id] });
      void qc.invalidateQueries({ queryKey: ['stock-entries'] });
      void navigate({ to: '/stock-entries/$id', params: { id } });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: 'Не удалось обновить документ',
        description: renderApiErrorMessage(err),
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  const se = query.data;
  const readOnly = se.status !== 'draft';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: 'Складские документы', to: '/stock-entries' },
              { label: se.voucher_no ?? id, to: `/stock-entries/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={m.stock_entry_edit_title()}
      />
      {readOnly && (
        <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')} role="status">
          {m.error_entity_not_editable({
            current_state: se.status,
            editable_states: 'draft',
          })}
        </div>
      )}
      <div className={shared.formColumn480}>
        <DatePicker
          label={m.stock_entry_field_posting_date()}
          value={form.posting_date || null}
          onChange={(next) => setForm({ ...form, posting_date: next ?? '' })}
          disabled={readOnly}
        />
        <Textarea
          label={m.stock_entry_field_notes()}
          value={form.notes_ru}
          onChange={(e) => setForm({ ...form, notes_ru: e.target.value })}
          disabled={readOnly}
          rows={3}
        />
      </div>
      {/* FE-62: line editor — current lines display (read-only) с guidance. */}
      <div style={{ marginTop: 'var(--space-4)' }}>
        <h3 className={shared.sectionTitle}>Позиции ({se.lines?.length ?? 0})</h3>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginBottom: 8 }}>
          Редактирование позиций — через мастер «+ Новый документ» с copy-from-here. Header (дата +
          примечание) edit'аются здесь.
        </p>
        {(se.lines ?? []).length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-surface)', textAlign: 'left' }}>
                <th style={{ padding: '6px 10px' }}>№</th>
                <th style={{ padding: '6px 10px' }}>{m.ui_poziciya()}</th>
                <th style={{ padding: '6px 10px' }}>{m.ui_sklad_istochnik()}</th>
                <th style={{ padding: '6px 10px' }}>{m.ui_sklad_poluchatel()}</th>
                <th style={{ padding: '6px 10px', textAlign: 'right' as const }}>
                  {m.ui_kol_vo()}
                </th>
                <th style={{ padding: '6px 10px' }}>{m.ui_ed_izm()}</th>
              </tr>
            </thead>
            <tbody>
              {(se.lines ?? []).map((l, i) => (
                <tr key={`l-${i}`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{i + 1}</td>
                  <td style={{ padding: '6px 10px' }}>
                    {(l as { item_name?: string }).item_name ??
                      (l as { item_id?: string }).item_id?.slice(0, 8) ??
                      '—'}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {(l as { source_warehouse_name?: string }).source_warehouse_name ?? '—'}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {(l as { target_warehouse_name?: string }).target_warehouse_name ?? '—'}
                  </td>
                  <td
                    style={{
                      padding: '6px 10px',
                      textAlign: 'right' as const,
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {(l as { qty?: string }).qty ?? '—'}
                  </td>
                  <td style={{ padding: '6px 10px' }}>{(l as { uom?: string }).uom ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
            {m.ui_dokument_ne_soderzhit_poziciy()}
          </p>
        )}
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/stock-entries/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={readOnly}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
